import { BarList } from '@/components/common/bar-list';
import { PageHeader } from '@/components/common/page-header';
import { QueryDegradedNotice } from '@/components/common/query-degraded-notice';
import { SectionCard } from '@/components/common/section-card';
import { EmptyState, ErrorState, TableSkeleton } from '@/components/fleet/states';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { usePopularRegions } from '@/hooks/use-popular-regions';
import { countryFlag } from '@/lib/country-flag';

const NUMBER = new Intl.NumberFormat('en-US');

export function RegionsAnalyticsPage(): JSX.Element {
  const { data, isLoading, isError, error, refetch } = usePopularRegions();

  return (
    <div className="space-y-6">
      <PageHeader
        title="Regions"
        description="Where connected users are exiting right now, ranked by active sessions."
      />

      <SectionCard title="Active sessions by region">
        {isLoading && <TableSkeleton rows={6} />}

        {isError && (
          <ErrorState
            title="Couldn't load regions."
            error={error}
            onRetry={() => void refetch()}
          />
        )}

        {data?.degraded && <QueryDegradedNotice />}

        {data && !data.degraded && (
          <BarList
            items={data.regions.map((r) => ({
              label: r.country_code
                ? `${countryFlag(r.country_code)} ${r.country_code}`.trim()
                : 'Unknown',
              hint: r.continent ?? undefined,
              value: r.active_sessions,
            }))}
            valueFormat={(v) => NUMBER.format(v)}
            emptyMessage="No active sessions yet."
          />
        )}
      </SectionCard>

      <SectionCard title="Region detail">
        {isLoading && <TableSkeleton rows={6} />}

        {isError && (
          <ErrorState
            title="Couldn't load regions."
            error={error}
            onRetry={() => void refetch()}
          />
        )}

        {data?.degraded && <QueryDegradedNotice />}

        {data && !data.degraded && data.regions.length === 0 && (
          <EmptyState message="No active sessions yet" />
        )}

        {data && !data.degraded && data.regions.length > 0 && (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Country</TableHead>
                <TableHead>Continent</TableHead>
                <TableHead className="text-right">Active sessions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.regions.map((r) => (
                <TableRow key={r.country_code ?? 'unknown'}>
                  <TableCell>
                    {r.country_code ? (
                      <>
                        <span className="mr-1.5" aria-hidden>
                          {countryFlag(r.country_code)}
                        </span>
                        <span className="font-mono">{r.country_code}</span>
                      </>
                    ) : (
                      <span className="text-muted-foreground">Unknown</span>
                    )}
                  </TableCell>
                  <TableCell className="font-mono text-muted-foreground">
                    {r.continent ?? '—'}
                  </TableCell>
                  <TableCell className="text-right font-mono">
                    {NUMBER.format(r.active_sessions)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </SectionCard>
    </div>
  );
}
